import { DataGrid, DataGridRef, Search, Spinner } from '@cimplico/components';
import { RefObject, useRef, useState } from 'react';
import { useFYIDocuments } from '../../../../../../queries/use-fyi-documents';
import { CheckIcon, DocumentIcon } from '@heroicons/react/20/solid';
import Breadcrumbs from '../../../../../../components/breadcrumbs/Breadcrumbs';
import { Cabinet, FYIDocument } from '../../../../../../types/fyi-docs';
import { FYI_URL } from '../../../../../../config';

type FYICabinetDocumentsTableProps = {
    cabinet: Cabinet;
    fyiEntityId: number;
    onClose: () => void;
    onSelect: (documents: FYIDocument[]) => void;
    tableRef: RefObject<DataGridRef>;
};

function FYICabinetDocumentsTable({ cabinet, fyiEntityId, onClose, onSelect, tableRef }: FYICabinetDocumentsTableProps): JSX.Element {
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(100);
    const [name, setName] = useState<string>();

    const [selected, setSelected] = useState(0);

    const fyiDocumentQuery = useFYIDocuments({ fyiCabinetId: cabinet.id, fyiEntityId, page, limit, name });

    if (fyiDocumentQuery.isLoading) {
        return (
            <div className="flex flex-col items-center gap-2 mt-16">
                <Spinner />
                <p className="text-sm text-gray-600">Loading documents...</p>
            </div>
        );
    }

    if (fyiDocumentQuery.isError || !fyiDocumentQuery.isSuccess) {
        return (
            <div className="flex flex-col items-center gap-2 mt-16">
                <p className="text-sm text-gray-600">Unable to get documents</p>
            </div>
        );
    }

    const { documents, totalItems } = fyiDocumentQuery.data;

    function handleSelect(ids: string[]): void {
        const docs = documents.filter(({ document }) => ids.includes(document.id)).map(({ document }) => document);
        setSelected(docs.length);
        onSelect(docs || []);
    }

    return (
        <div>
            <div className="pb-4 flex flex-col gap-2 px-1">
                <Breadcrumbs
                    sections={[
                        { label: 'Cabinets', value: 'cabinets' },
                        { label: cabinet.name, value: cabinet.name, selected: true },
                    ]}
                    onClick={(val): void => {
                        if (val === 'cabinets') {
                            onClose();
                        }
                    }}
                />
                <Search
                    size="small"
                    placeholder="Search name..."
                    onChange={(s): void => {
                        if (s !== name) {
                            setPage(1);
                            tableRef.current?.resetPagination();
                            setName(s);
                        }
                    }}
                />
            </div>
            <p className="flex items-center text-sm text-gray-600 mb-1 px-1">Files Selected: {selected} / 100</p>
            <DataGrid
                columns={[{ width: '25px' }, { header: 'Name' }, { header: 'Previously selected', alignment: 'center' }]}
                rowSizes={[100, 200, 500]}
                totalRows={totalItems}
                onPageChange={setPage}
                onRowSizeChange={setLimit}
                isLoading={fyiDocumentQuery.isLoading || fyiDocumentQuery.isFetching}
                onSelect={handleSelect}
                limitDirection="top"
                stickyHeader
                ref={tableRef}
            >
                {documents.map(({ document, exists }) => {
                    return (
                        <DataGrid.Row id={document.id} key={document.id} disabled={exists}>
                            <DocumentIcon className="text-gray-700 h-5 w-5 shrink-0" />

                            <a
                                href={`${FYI_URL}/documents/${document.id}/preview`}
                                target="_blank"
                                className="text-sm text-indigo-600 font-medium py-1"
                                rel="noreferrer"
                            >
                                {document.name}
                            </a>

                            <div>{exists ? <CheckIcon className="text-indigo-600 h-4 w-4" /> : <></>}</div>
                        </DataGrid.Row>
                    );
                })}
            </DataGrid>
        </div>
    );
}
export default FYICabinetDocumentsTable;
